import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, User, Building, Mail, Phone, Ticket, FileText, 
  Calendar, Clock, Download, CheckCircle, XCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LttBookingDetail = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const topicLabels = {
    individual_tax: 'Individual Tax Question',
    business_tax: 'Business Tax Question',
    accounting: 'Accounting / Bookkeeping',
    irs_notice: 'IRS or State Tax Notice',
    estimated_taxes: 'Estimated Taxes / Safe-Harbor',
    entity_setup: 'Entity Setup or Change',
    other: 'Other'
  };

  useEffect(() => {
    fetchBookingDetail();
  }, [bookingId]);

  const fetchBookingDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('leadboard_session_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/letstalktaxes-bookings?booking_id=${bookingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.bookings && data.bookings.length > 0) {
          setBooking(data.bookings[0]);
        } else {
          toast.error('Booking not found');
          navigate('/leadboard');
        }
      } else {
        toast.error('Failed to fetch booking details');
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('This will redeem the customer coupon via LetsTalkTaxes API. Continue?')) {
      return;
    }

    setApproving(true);

    try {
      const token = localStorage.getItem('leadboard_session_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/letstalktaxes/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ booking_id: bookingId, admin_notes: '' })
      });

      const data = await response.json();

      if (response.ok && data.approved) {
        toast.success(`Booking approved! Coupon redeemed for ${data.api_response?.customer_name || 'customer'}`);
        fetchBookingDetail(); // Refresh
      } else {
        toast.error(data.detail || 'Approval failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setRejecting(true);

    try {
      const token = localStorage.getItem('leadboard_session_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/letstalktaxes/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: bookingId,
          rejection_reason: rejectionReason
        })
      });

      const data = await response.json();

      if (response.ok && data.rejected) {
        toast.success('Booking rejected. Coupon remains available for customer.');
        fetchBookingDetail(); // Refresh
        setShowRejectForm(false);
        setRejectionReason('');
      } else {
        toast.error(data.detail || 'Rejection failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm} EST`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <p className="text-[#4C5A70]">Loading booking details...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <p className="text-[#4C5A70]">Booking not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Back Button */}
        <Button data-testid="ltt-booking-detail-button-5"
          asChild
          variant="ghost"
          className="mb-6 text-[#0B2240]"
        >
          <Link data-testid="ltt-booking-detail-link-1" to="/leadboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to LeadBoard
          </Link>
        </Button>

        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0B2240] mb-2">
              Consultation Booking Details
            </h1>
            <p className="text-[#4C5A70]">Booking ID: {bookingId}</p>
          </div>
          <div className="flex gap-2">
            <Badge className={
              booking.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
              booking.status === 'approved' ? 'bg-green-100 text-green-800' :
              booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
              booking.status === 'rejected' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }>
              {booking.status.replace('_', ' ').toUpperCase()}
            </Badge>
            {booking.coupon_redeemed && (
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Coupon Redeemed
              </Badge>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Customer Information */}
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-xl flex items-center gap-2">
                <User className="w-5 h-5 text-[#D4AF37]" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#4C5A70] mb-1">Name</p>
                  <p className="font-medium text-[#0B2240]">{booking.customer_name}</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Client Type</p>
                  <p className="font-medium text-[#0B2240] flex items-center gap-1">
                    {booking.client_type === 'business' ? (
                      <><Building className="w-4 h-4" /> Business</>
                    ) : (
                      <><User className="w-4 h-4" /> Individual</>
                    )}
                  </p>
                </div>
                {booking.business_name && (
                  <div>
                    <p className="text-[#4C5A70] mb-1">Business Name</p>
                    <p className="font-medium text-[#0B2240]">{booking.business_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-[#4C5A70] mb-1">Email</p>
                  <p className="font-medium text-[#0B2240]">{booking.email}</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Phone</p>
                  <p className="font-medium text-[#0B2240]">{booking.phone}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Coupon Information */}
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-xl flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#D4AF37]" />
                Coupon Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-[#4C5A70] mb-1">Code</p>
                  <p className="font-mono font-medium text-[#0B2240]">{booking.coupon_code}</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Value</p>
                  <p className="font-medium text-[#D4AF37]">$49.00</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Redeemed</p>
                  <p className="font-medium text-[#0B2240]">
                    {booking.coupon_redeemed ? '✅ Yes' : '❌ No (will redeem on approval)'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Consultation Details */}
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Consultation Details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-[#4C5A70] mb-1">Topic</p>
                  <p className="font-medium text-[#0B2240]">{topicLabels[booking.topic] || booking.topic}</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-2">Main Question</p>
                  <div className="bg-[#F7F6F2] p-4 rounded-lg">
                    <p className="text-[#0B2240]">{booking.main_question}</p>
                  </div>
                </div>
                {booking.supporting_details && (
                  <div>
                    <p className="text-[#4C5A70] mb-2">Additional Details</p>
                    <div className="bg-[#F7F6F2] p-4 rounded-lg">
                      <p className="text-[#0B2240]">{booking.supporting_details}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Appointment */}
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-xl flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#D4AF37]" />
                Appointment
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#4C5A70] mb-1">Date</p>
                  <p className="font-medium text-[#0B2240]">{formatDate(booking.appointment_date)}</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Time</p>
                  <p className="font-medium text-[#0B2240]">{formatTime(booking.appointment_time)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {booking.status === 'pending_approval' && (
            <Card className="border-2 border-[#E1DFD7]">
              <CardHeader className="border-b border-[#E1DFD7]">
                <CardTitle className="text-xl">Actions</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {!showRejectForm ? (
                  <div className="flex gap-3">
                    <Button data-testid="ltt-booking-detail-button-4"
                      onClick={() => setShowRejectForm(true)}
                      variant="outline"
                      className="flex-1 h-12 border-2 border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button data-testid="ltt-booking-detail-button-3"
                      onClick={handleApprove}
                      disabled={approving}
                      className="flex-1 h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]"
                    >
                      {approving ? (
                        'Calling API...'
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve & Redeem Coupon
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Label>Rejection Reason</Label>
                    <Textarea data-testid="ltt-booking-detail-textarea-1"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      className="min-h-[100px]"
                    />
                    <div className="flex gap-3">
                      <Button data-testid="ltt-booking-detail-button-2"
                        onClick={() => {
                          setShowRejectForm(false);
                          setRejectionReason('');
                        }}
                        variant="outline"
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button data-testid="ltt-booking-detail-button-1"
                        onClick={handleReject}
                        disabled={rejecting || !rejectionReason.trim()}
                        className="flex-1 bg-red-600 text-white hover:bg-red-700"
                      >
                        {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Show rejection reason if rejected */}
          {booking.status === 'rejected' && booking.rejection_reason && (
            <Card className="border-2 border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <p className="text-sm font-medium text-red-900 mb-1">Rejection Reason:</p>
                <p className="text-sm text-red-800">{booking.rejection_reason}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LttBookingDetail;
